import { useTranslation } from 'react-i18next'
import { Button } from '@structural-climbing/ui'
import { MemberAvatar } from '../members/MemberAvatar'
import { useGymThemeContext } from '../../layouts/useGymTheme'
import { cartCount, cartName, cartTotal, formatMoney } from './money'
import type { Cart, Product } from './types'

interface Props {
  carts: Cart[]
  activeCartId: string | null
  productsById: Map<string, Product>
  currency: string
  onSelect: (cartId: string) => void
  /** Leaves every cart, so the next product tapped starts a quick sale. */
  onNewSale: () => void
  /** Opens a cart for a member, or a named walk-in. */
  onNewMemberCart: () => void
}

/**
 * Every open cart, side by side. Switching between climbers is one tap, and
 * each one shows what it holds so the desk can see who still owes what.
 */
export function CartStrip({
  carts,
  activeCartId,
  productsById,
  currency,
  onSelect,
  onNewSale,
  onNewMemberCart,
}: Props) {
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()

  return (
    <div
      role="group"
      aria-label={t('products.openCarts')}
      className="scrollbar-hide flex items-stretch gap-2 overflow-x-auto pb-1"
    >
      {/* Selected while no cart is: that is where the next tap goes. */}
      <Button
        color="primary"
        variant={activeCartId === null ? 'solid' : 'bordered'}
        aria-pressed={activeCartId === null}
        className="h-14 shrink-0 font-semibold"
        onPress={onNewSale}
      >
        + {t('products.newSale')}
      </Button>
      <Button variant="bordered" className="h-14 shrink-0" onPress={onNewMemberCart}>
        + {t('products.memberCart')}
      </Button>

      {carts.map((cart) => {
        const active = cart.id === activeCartId
        const name = cartName(cart) || t('products.guest')
        const count = cartCount(cart)
        return (
          <Button
            key={cart.id}
            color={active ? 'primary' : 'default'}
            variant={active ? 'solid' : 'flat'}
            aria-pressed={active}
            className="h-14 shrink-0 justify-start gap-2 px-3"
            onPress={() => onSelect(cart.id)}
          >
            <MemberAvatar
              userId={cart.user_id}
              name={name}
              avatarExt={cart.member?.avatar_ext ?? null}
              avatarUpdatedAt={cart.member?.avatar_updated_at ?? null}
              primaryColor={primaryColor}
            />
            <span className="flex min-w-0 flex-col items-start">
              <span className="max-w-36 truncate text-sm font-medium">{name}</span>
              <span className="text-xs opacity-70">
                {count === 0
                  ? t('products.itemCount', { count: 0 })
                  : `${t('products.itemCount', { count })} · ${formatMoney(cartTotal(cart, productsById), currency)}`}
              </span>
            </span>
          </Button>
        )
      })}
    </div>
  )
}
